import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { NavigationBar } from '../components/NavigationBar';
import { useTheme } from '../context/ThemeContext';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store/store';
import { setCredentials } from '../store/authSlice';
import { subscriptionService } from '../services/subscriptionService';

export const Settings = () => {
  const { darkMode, setDarkMode, canUseDarkMode } = useTheme();
  const user = useSelector((state: RootState) => state.auth.user);
  const dispatch = useDispatch();
  const [settings, setSettings] = useState({
    emailNotifications: true,
    language: 'en',
    quizAutoSubmit: true,
  });
  
  // Add subscription states
  const [subscriptionStatus, setSubscriptionStatus] = useState({
    isPremium: false,
    premiumExpiresAt: null as Date | null,
    subscriptionActive: false,
    subscriptionCanceled: false,
    subscriptionId: null as string | null
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);
  const [cancelSuccess, setCancelSuccess] = useState<{ success: boolean; message: string; expiresAt?: Date } | null>(null);

  // Fetch subscription status
  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      try {
        const status = await subscriptionService.checkSubscriptionStatus();
        setSubscriptionStatus(status);
      } catch (error) {
        console.error('Error fetching subscription status:', error);
      }
    };

    if (user?.isPremium) {
      fetchSubscriptionStatus();
    }
  }, [user]);

  // Handle cancel subscription
  const handleCancelSubscription = async () => {
    setIsLoading(true);
    try {
      const result = await subscriptionService.cancelSubscription();
      setCancelSuccess(result);
      setShowConfirmCancel(false);
      
      // Update subscription status
      setSubscriptionStatus(prev => ({
        ...prev,
        subscriptionCanceled: true,
        premiumExpiresAt: result.expiresAt ? new Date(result.expiresAt) : prev.premiumExpiresAt
      }));
      
      // Update user in Redux (they remain premium until period end)
      if (user) {
        dispatch(setCredentials({ 
          user: { 
            ...user, 
            premiumExpiresAt: result.expiresAt 
          }, 
          token: localStorage.getItem('token') || '' 
        }));
      }
    } catch (error: any) {
      setCancelSuccess({ 
        success: false, 
        message: error.response?.data?.message || 'Failed to cancel subscription. Please try again.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (key: keyof typeof settings, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 dark:text-gray-100">
      <NavigationBar />
      <div className="pt-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="bg-white/60 dark:bg-gray-800/60 backdrop-blur-lg rounded-2xl shadow-xl p-8 dark:text-gray-100 dark:border dark:border-gray-700">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Settings</h1>

              <div className="space-y-6">
                <div className="bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm rounded-xl p-6 shadow-sm dark:border dark:border-gray-600">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Preferences</h2>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-medium text-gray-900 dark:text-white">Email Notifications</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-300">Receive updates about your quizzes</p>
                      </div>
                      <button
                        onClick={() => handleChange('emailNotifications', !settings.emailNotifications)}
                        className={`${
                          settings.emailNotifications ? 'bg-indigo-600' : 'bg-gray-200 dark:bg-gray-600'
                        } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out`}
                      >
                        <span
                          className={`${
                            settings.emailNotifications ? 'translate-x-6' : 'translate-x-1'
                          } inline-block h-4 w-4 transform rounded-full bg-white transition duration-200 ease-in-out mt-1`}
                        />
                      </button>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-medium text-gray-900 dark:text-white">Dark Mode</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-300">
                          {canUseDarkMode ? 'Enable dark theme' : 'Upgrade to premium to enable dark theme'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {!canUseDarkMode && (
                          <a
                            href="/pricing"
                            className="text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-500"
                          >
                            Upgrade
                          </a>
                        )}
                        <button
                          onClick={() => setDarkMode(!darkMode)}
                          disabled={!canUseDarkMode}
                          className={`${
                            darkMode ? 'bg-indigo-600' : 'bg-gray-200 dark:bg-gray-600'
                          } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out ${
                            !canUseDarkMode && 'opacity-50 cursor-not-allowed'
                          }`}
                        >
                          <span
                            className={`${
                              darkMode ? 'translate-x-6' : 'translate-x-1'
                            } inline-block h-4 w-4 transform rounded-full bg-white transition duration-200 ease-in-out mt-1`}
                          />
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-900 dark:text-white">Language</label>
                      <select
                        value={settings.language}
                        onChange={(e) => handleChange('language', e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                      >
                        <option value="en">English</option>
                        <option value="es">Spanish</option>
                        <option value="fr">French</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm rounded-xl p-6 shadow-sm dark:border dark:border-gray-600">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Quiz Settings</h2>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-medium text-gray-900 dark:text-white">Auto Submit</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-300">Automatically submit quiz when time expires</p>
                      </div>
                      <button
                        onClick={() => handleChange('quizAutoSubmit', !settings.quizAutoSubmit)}
                        className={`${
                          settings.quizAutoSubmit ? 'bg-indigo-600' : 'bg-gray-200 dark:bg-gray-600'
                        } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out`}
                      >
                        <span
                          className={`${
                            settings.quizAutoSubmit ? 'translate-x-6' : 'translate-x-1'
                          } inline-block h-4 w-4 transform rounded-full bg-white transition duration-200 ease-in-out mt-1`}
                        />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Subscription Management Section */}
                {user?.isPremium && (
                  <div className="bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm rounded-xl p-6 shadow-sm dark:border dark:border-gray-600">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Premium Subscription</h2>
                    
                    <div className="space-y-4">
                      <div className="flex flex-col space-y-2">
                        <div className="flex items-center">
                          <div className="px-3 py-1 bg-gradient-to-r from-yellow-400 to-yellow-600 text-white text-xs font-bold rounded-full flex items-center">
                            PRO
                          </div>
                          <span className="ml-2 text-sm font-medium text-gray-900 dark:text-white">
                            Premium Subscription
                          </span>
                        </div>

                        {subscriptionStatus.premiumExpiresAt && (
                          <p className="text-sm text-gray-600 dark:text-gray-300">
                            {subscriptionStatus.subscriptionCanceled 
                              ? `Your subscription will end on ${new Date(subscriptionStatus.premiumExpiresAt).toLocaleDateString()}`
                              : `Renews on ${new Date(subscriptionStatus.premiumExpiresAt).toLocaleDateString()}`
                            }
                          </p>
                        )}

                        {/* Show success message */}
                        {cancelSuccess && (
                          <div className={`mt-2 p-3 rounded-md text-sm ${
                            cancelSuccess.success 
                              ? "bg-green-100 text-green-800 dark:bg-green-800/30 dark:text-green-200" 
                              : "bg-red-100 text-red-800 dark:bg-red-800/30 dark:text-red-200"
                          }`}>
                            {cancelSuccess.message}
                          </div>
                        )}

                        {/* Modified to always show cancel button for premium users if not canceled */}
                        {!subscriptionStatus.subscriptionCanceled && (
                          <div className="mt-4">
                            {!showConfirmCancel ? (
                              <button
                                onClick={() => setShowConfirmCancel(true)}
                                className="text-sm font-medium text-red-600 hover:text-red-500 dark:text-red-400 dark:hover:text-red-300"
                              >
                                Cancel Subscription
                              </button>
                            ) : (
                              <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-md border border-red-100 dark:border-red-800/30">
                                <p className="text-sm text-red-800 dark:text-red-200 mb-3">
                                  Are you sure you want to cancel your subscription? You'll continue to have premium access until the end of your current billing period.
                                </p>
                                <div className="flex space-x-3">
                                  <button
                                    onClick={() => setShowConfirmCancel(false)}
                                    className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
                                  >
                                    Keep Subscription
                                  </button>
                                  <button
                                    onClick={handleCancelSubscription}
                                    disabled={isLoading}
                                    className="text-sm font-medium text-red-600 hover:text-red-500 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50"
                                  >
                                    {isLoading ? 'Processing...' : 'Confirm Cancellation'}
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {subscriptionStatus.subscriptionCanceled && (
                          <div className="mt-4">
                            <a 
                              href="/pricing" 
                              className="text-sm font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
                            >
                              Renew Subscription
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm rounded-xl p-6 shadow-sm dark:border dark:border-gray-600">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Account</h2>
                  
                  <div className="space-y-4">
                    <button
                      className="text-sm text-red-600 hover:text-red-500 dark:text-red-400 dark:hover:text-red-300"
                    >
                      Delete Account
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};